import { useEffect, useRef } from 'react';
import type { CrmUser, ProjectUpdate } from '../../types/crm';
import { getProjectAttachmentUrl } from '../../lib/projectUpdates';
import {
  authorDisplayName,
  authorRoleLabel,
  formatProjectUpdateTime,
  isOwnChatMessage,
  isSystemProjectUpdate,
} from '../../utils/projectHelpers';

interface ProjectUpdatesTimelineProps {
  updates: ProjectUpdate[];
  users: CrmUser[];
  currentUsername: string;
}

export function ProjectUpdatesTimeline({
  updates,
  users,
  currentUsername,
}: ProjectUpdatesTimelineProps) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [updates.length]);

  if (updates.length === 0) {
    return (
      <p className="project-timeline__empty">No chat history yet. Start the conversation!</p>
    );
  }

  return (
    <div className="project-timeline">
      {updates.map((update) => {
        const system = isSystemProjectUpdate(update.content);
        const authorId = update.author_id ?? update.author_name ?? 'System';
        const isMe = !system && isOwnChatMessage(authorId, currentUsername);
        const side = system ? 'center' : isMe ? 'end' : 'start';
        const role = !system && !isMe ? authorRoleLabel(users, authorId) : null;

        return (
          <div key={update.id} className={`project-timeline__row project-timeline__row--${side}`}>
            <article
              className={`project-timeline__bubble project-timeline__bubble--${
                system ? 'system' : isMe ? 'me' : 'other'
              }`}
            >
              {!system && (
                <p className="project-timeline__author">
                  <span>{authorDisplayName(update, users)}</span>
                  {role && <span className="project-timeline__role">{role}</span>}
                </p>
              )}
              <p className="project-timeline__content">{update.content}</p>
              {update.attachment_url && update.attachment_name && (
                <button
                  type="button"
                  className="project-timeline__attachment"
                  onClick={() => void openAttachment(update.attachment_url!)}
                >
                  📎 {update.attachment_name}
                </button>
              )}
              <time className="project-timeline__time" dateTime={update.created_at}>
                {formatProjectUpdateTime(update.created_at)}
              </time>
            </article>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

async function openAttachment(path: string) {
  const url = await getProjectAttachmentUrl(path);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}
